import { useCallback, useEffect, useState } from "react";
import {
  MessageSquareReply, Boxes, CalendarClock, ScanLine, BarChart3, Infinity as InfinityIcon,
  FileText, Mail, CheckCircle2, ChevronDown, Clock, Receipt, AlertTriangle, Ban, Gift, Download, Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtShortDate } from "../lib/format";
import { HISTORY_LABEL, kr, krOre, INVOICE_STATUS, downloadPdf } from "../lib/billing";
import { useFlash } from "../lib/flash";
import BillingForm from "../components/BillingForm";
import InvoiceView from "../components/InvoiceView";
import { Pill } from "../components/bits";

const FEATURES = [
  [InfinityIcon, "Ubegrænset antal tilbud og ordrer – intet gebyr pr. salg"],
  [MessageSquareReply, "Robot der svarer kunderne på Facebook med det samme"],
  [Boxes, "Lager, max pr. kunde og automatisk venteliste"],
  [CalendarClock, "Kundeside med afbud, kalender og SMS-påmindelser"],
  [ScanLine, "Udleveringsskærm, QR-scanning og plukliste"],
  [BarChart3, "Statistik og indsigt"],
];

const faq = (email, dueDays) => [
  ["Er der binding?", "Nej. Abonnementet løber måned for måned, og I kan stoppe når som helst."],
  ["Hvordan opsiger jeg?", `Opsigelse: skriv til ${email}, så stopper vi abonnementet ved udgangen af den betalte måned.`],
  ["Er prisen med moms?", "Prisen er 399 kr. pr. måned ekskl. moms. Med 25 % moms bliver det 498,75 kr. pr. måned."],
  ["Hvornår skal fakturaen betales?", `Betalingsfristen er ${dueDays} dage. I får en ny faktura på mail ved starten af hver måned.`],
];

function StatusCard({ a, price, priceVat, trialDays }) {
  const k = a.kind;
  const S = {
    trial: [Clock, "trial", "Prøveperiode", `${a.daysLeft} ${a.daysLeft === 1 ? "dag" : "dage"} tilbage – til ${fmtDate(a.until)}.`],
    expired: [AlertTriangle, "bad", "Prøveperioden er udløbet", a.pending ? "Fakturaen er på vej – I får den på mail snarest." : "Start abonnementet for at fortsætte med JaTak Pro."],
    past_due: [Receipt, a.locked ? "bad" : "warn", a.overdue ? "Faktura forfalden" : "Faktura sendt – afventer betaling",
      `${a.unpaid} ubetalt${a.unpaid === 1 ? "" : "e"} faktura${a.unpaid === 1 ? "" : "er"} · forfald ${fmtShortDate(a.oldest_due)}.`],
    cancelled: [Ban, a.locked ? "bad" : "warn", "Abonnementet er stoppet", a.period_end && !a.locked ? `I har adgang til og med ${fmtShortDate(a.period_end)}.` : "Skriv til os, hvis I vil starte igen."],
    active: [CheckCircle2, "ok", "Aktivt abonnement",
      a.paid_at ? `Betalt ${fmtShortDate(a.paid_at)}${a.next_period ? ` · næste periode starter ${fmtShortDate(a.next_period)}` : ""}.` : "Tak fordi I bruger JaTak Pro."],
    free: [Gift, "ok", "Gratis", "I har fuld adgang uden betaling."],
    suspended: [Ban, "bad", "Suspenderet", "Kontoen er midlertidigt spærret. Skriv til info@jatakpro.dk."],
  }[k] || [Clock, "trial", k, ""];
  const [Icon, tone, title, text] = S;
  const total = Math.max(trialDays, a.daysLeft || 0);
  const pct = k === "trial" ? Math.min(100, Math.max(4, ((total - a.daysLeft) / total) * 100)) : 0;
  return (
    <section className={`panel bl-status ${tone}`} data-testid="billing-status-card">
      <div className="bl-status-top">
        <span className="bl-ico" aria-hidden="true"><Icon size={22} /></span>
        <div>
          <span className={`bl-badge ${tone}`} data-testid="billing-status-badge">{title}</span>
          <p className="bl-text" data-testid="billing-status-text">{text}</p>
        </div>
      </div>
      {k === "trial" && (
        <div className="bl-progress" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} data-testid="billing-trial-progress">
          <span style={{ width: `${pct}%` }} />
        </div>
      )}
      {a.locked && <p className="bl-locked" data-testid="billing-locked-note">Adgangen til JaTak Pro er låst, indtil betalingen er registreret.</p>}
      {a.pending && (
        <p className="bl-req" data-testid="billing-pending-note"><Mail size={15} /> Fakturaen er på vej – I får den på mail snarest.</p>
      )}
      {a.unpaid > 0 && k !== "past_due" && (
        <p className="bl-req" data-testid="billing-unpaid-note">
          <Receipt size={15} /> Faktura sendt – afventer betaling (forfald {fmtShortDate(a.oldest_due)}).
        </p>
      )}
    </section>
  );
}

function PlanCard({ price, priceVat }) {
  return (
    <section className="panel bl-plan" data-testid="billing-plan-card">
      <h2>JaTak Pro</h2>
      <p className="bl-price"><b>{kr(price)}</b><span>/md. ekskl. moms</span></p>
      <p className="muted bl-vat" data-testid="billing-price-vat">{kr(priceVat)} inkl. moms · ingen binding · månedlig faktura</p>
      <ul className="bl-features">
        {FEATURES.map(([Icon, t]) => (
          <li key={t}><span className="bl-fi"><Icon size={16} /></span>{t}</li>
        ))}
      </ul>
    </section>
  );
}

function Steps() {
  const steps = [
    [FileText, "Start abonnement", "Udfyld jeres firmaoplysninger – fakturaen dannes med det samme."],
    [Mail, "Fakturaen kommer på mail", "Til den faktura-e-mail, I har angivet – med en knap til at hente PDF'en."],
    [CheckCircle2, "Betal – så er adgangen aktiv", "Når betalingen er registreret, kører alt videre."],
  ];
  return (
    <section className="panel" data-testid="billing-steps">
      <h2>Sådan fungerer betaling</h2>
      <ol className="bl-steps">
        {steps.map(([Icon, t, d], i) => (
          <li key={t}><span className="bl-num">{i + 1}</span><div><b><Icon size={15} /> {t}</b><span>{d}</span></div></li>
        ))}
      </ol>
    </section>
  );
}

function Invoices({ items }) {
  const [view, setView] = useState(null);
  const flash = useFlash();
  const dl = (i) => downloadPdf(`/billing/invoices/${i.id}/pdf`, i.number).catch(() => flash("Kunne ikke hente PDF.", "error"));
  return (
    <section className="panel" data-testid="billing-invoices">
      <h2>Fakturaer</h2>
      {items.length ? items.map((i) => {
        const [cls, label] = INVOICE_STATUS[i.status] || ["draft", i.status];
        return (
          <div className="bl-inv" key={i.id} data-testid={`billing-invoice-${i.number}`}>
            <div className="bl-inv-main">
              <b>Nr. {i.number}</b>
              <span className="muted" data-testid={`billing-invoice-date-${i.number}`}>Dato {fmtShortDate(i.issue_date)} · {i.period_label}</span>
              <span className="muted">Forfald {fmtShortDate(i.due_date)}</span>
            </div>
            <div className="bl-inv-side">
              <b className="num">{krOre(i.total_ore)}</b>
              <span data-testid={`billing-invoice-status-${i.number}`}><Pill status={cls} label={label} /></span>
            </div>
            <div className="bl-inv-acts">
              <button className="btn small" onClick={() => setView(i)} data-testid={`billing-invoice-view-${i.number}`}><Eye size={14} /> Vis</button>
              <button className="btn small" onClick={() => dl(i)} data-testid={`billing-invoice-pdf-${i.number}`}><Download size={14} /> Hent PDF</button>
            </div>
          </div>
        );
      }) : <p className="muted" data-testid="billing-invoices-empty">Ingen fakturaer endnu.</p>}
      {view && <InvoiceView inv={view} onClose={() => setView(null)} onDownload={() => dl(view)} />}
    </section>
  );
}

function History({ items }) {
  if (!items.length) return null;
  return (
    <section className="panel" data-testid="billing-history">
      <h2>Tidligere registreringer</h2>
      {items.length ? items.map((h) => (
        <div className="bl-hrow" key={h.id} data-testid={`billing-history-${h.id}`}>
          <span>{fmtShortDate(h.created_at)}</span>
          <span className={`pill ${h.kind === "paid" ? "picked_up" : h.kind === "sent" ? "pending" : "draft"}`}>{HISTORY_LABEL[h.kind] || h.kind}</span>
          <span className="num">{kr(h.amount_dkk)}</span>
        </div>
      )) : <p className="muted" data-testid="billing-history-empty">Ingen fakturaer endnu.</p>}
    </section>
  );
}

function Faq({ email, dueDays }) {
  return (
    <section className="panel" data-testid="billing-faq">
      <h2>Spørgsmål</h2>
      {faq(email, dueDays).map(([q, a]) => (
        <details className="bl-faq" key={q}>
          <summary data-testid="billing-faq-q">{q}<ChevronDown size={18} /></summary>
          <p>{a}</p>
        </details>
      ))}
    </section>
  );
}

function SubscribePanel({ d, onSaved }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState("");
  const a = d.access;
  const started = a.started || a.pending || ["active", "free", "cancelled", "past_due"].includes(a.kind) || d.invoices.length > 0;
  if (["free", "suspended"].includes(a.kind) && !d.billing_info) return null;
  return (
    <section className="panel bl-request" id="faktura" data-testid="billing-subscribe-panel">
      <h2>{started ? "Faktureringsoplysninger" : "Start abonnement"}</h2>
      {done && <div className="notice" role="status" data-testid="billing-subscribe-confirm">{done}</div>}
      {open ? (
        <BillingForm initial={d.billing_info} pending={started} onCancel={() => setOpen(false)}
          onDone={(r) => { setOpen(false); setDone(r.flash); onSaved(); }} />
      ) : (
        <>
          {d.billing_info ? (
            <p className="muted bl-info" data-testid="billing-info-summary">
              {d.billing_info.company} · CVR {d.billing_info.cvr} · {d.billing_info.email}
            </p>
          ) : (
            <p className="muted">Udfyld jeres firmaoplysninger. Fakturaen dannes med det samme og sendes på mail{a.kind === "trial" ? " – abonnementet starter først, når prøveperioden slutter" : ""}.</p>
          )}
          {started && <p className="muted" style={{ fontSize: 13 }}>Ændringer bruges på kommende fakturaer – eksisterende fakturaer ændres ikke.</p>}
          <button className={"btn big" + (started ? "" : " primary")} onClick={() => { setDone(""); setOpen(true); }} data-testid="billing-subscribe-btn">
            {started ? "Ret faktureringsoplysninger" : "Start abonnement"}
          </button>
        </>
      )}
    </section>
  );
}

export default function Billing() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const { refresh } = useAuth();
  const load = useCallback(() => api.get("/billing").then((r) => setD(r.data)).catch(() => setErr(true)), []);
  useEffect(() => { load(); }, [load]);
  if (err && !d) return <div className="loading">Kunne ikke hente abonnement. <button className="btn small" onClick={() => { setErr(false); load(); }}>Prøv igen</button></div>;
  if (!d) return <div className="loading">Indlæser…</div>;
  const a = d.access;
  return (
    <div className="bl-page" data-testid="billing-page">
      <div className="page-head"><div><h1>Abonnement</h1><p>Én pris, alt med – betaling via faktura. <Link to="/priser" data-testid="billing-pricing-link">Se alt, der er med i prisen</Link></p></div></div>
      <StatusCard a={a} price={d.price_monthly} priceVat={d.price_monthly_incl_vat} trialDays={d.trial_days} />
      {d.billing_info_needed && (
        <div className="notice warn" data-testid="billing-info-needed">
          <b>Udfyld fakturaoplysninger.</b> Butikken er nu fakturamodtager, men mangler firmanavn, adresse, CVR og faktura-e-mail. Indtil da sendes fakturaen til jeres login-e-mail.
        </div>
      )}
      {d.group?.size > 1 && (
        <div className="notice info" data-testid="billing-group-note">
          <b>Samlet faktura for {d.group.size} butikker:</b> {d.group.stores.join(", ")} – {d.group.monthly_net} kr./md. ekskl. moms ({d.price_monthly} kr. pr. butik).
          Fakturaen sendes til {d.group.primary}s fakturaoplysninger, og betalingen gælder alle butikkerne. <Link to="/butikker">Se butikker</Link>
        </div>
      )}
      <div className="grid cols-2 bl-grid">
        <div className="bl-col">
          <SubscribePanel d={d} onSaved={() => { load(); refresh(); }} />
          <Invoices items={d.invoices} />
          <Steps />
          <History items={d.history} />
        </div>
        <div className="bl-col">
          <PlanCard price={d.price_monthly} priceVat={d.price_monthly_incl_vat} />
          <Faq email={d.contact_email} dueDays={d.due_days} />
        </div>
      </div>
    </div>
  );
}
